import { useMemo } from "react"
import { Link } from "react-router-dom"
import { ExternalLink } from "lucide-react"
import { CellContext, PaginationState } from "@tanstack/react-table"
import Table from "../../table"
import DateFilter, { DateRangePreset } from "../../activity/date-filter"
import { BBColumnDef, WebsiteChatSession } from "../../../../types"
import { formatAdvisorDate } from "../../../utils/datetime"

interface Props {
  sessions: WebsiteChatSession[]
  brandMap: Record<string, { name: string }>
  isBrandsLoading: boolean
  isLoading: boolean
  totalRows: number
  pagination: PaginationState
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>
  pageCount: number
  onSearchChange: (value: string) => void
  datePreset: DateRangePreset
  customDateStart: number | null
  customDateEnd: number | null
  onDateChange: (
    range: DateRangePreset,
    startDate?: Date,
    endDate?: Date
  ) => void
  onClearDate: () => void
}

const WebsiteChatsTable = ({
  sessions,
  brandMap,
  isBrandsLoading,
  isLoading,
  totalRows,
  pagination,
  setPagination,
  pageCount,
  onSearchChange,
  datePreset,
  customDateStart,
  customDateEnd,
  onDateChange,
  onClearDate
}: Props) => {
  const columns = useMemo<BBColumnDef<WebsiteChatSession>[]>(
    () => [
      {
        id: "sessionId",
        header: "Session ID",
        accessorFn: row => row.subjectId,
        cell: ({ row }: CellContext<WebsiteChatSession, unknown>) => (
          <td className="py-4 pl-2 pr-3">
            <span className="text-base font-bold truncate max-w-xs block">
              {row.original.subjectId}
            </span>
            {isBrandsLoading ? (
              <div className="h-3 w-24 rounded bg-neutral-100 animate-pulse mt-1" />
            ) : (
              brandMap[row.original.brandId ?? ""]?.name && (
                <div className="text-xs text-neutral-400 truncate max-w-xs mt-0.5">
                  {brandMap[row.original.brandId ?? ""]!.name}
                </div>
              )
            )}
            <div className="sm:hidden text-xs text-neutral-400 mt-0.5">
              {row.original.threads.length} threads &middot;{" "}
              {row.original.messageCount} msgs &middot;{" "}
              {formatAdvisorDate(row.original.lastActivityAt)}
            </div>
          </td>
        )
      },
      {
        id: "lastActivity",
        header: "Last Activity",
        accessorFn: row => row.lastActivityAt,
        cell: ({ row }: CellContext<WebsiteChatSession, unknown>) => (
          <td className="hidden sm:table-cell py-4 pr-3 text-sm text-neutral-500 whitespace-nowrap">
            {formatAdvisorDate(row.original.lastActivityAt)}
          </td>
        ),
        meta: { headerClassName: "hidden sm:table-cell" }
      },
      {
        id: "threads",
        header: "Threads #",
        accessorFn: row => row.threads.length,
        cell: ({ row }: CellContext<WebsiteChatSession, unknown>) => (
          <td className="hidden sm:table-cell py-4 pr-3 text-sm text-neutral-500 text-center">
            {row.original.threads.length}
          </td>
        ),
        meta: { headerClassName: "hidden sm:table-cell text-center" },
        enableSorting: true
      },
      {
        id: "totalMessages",
        header: "Messages",
        accessorFn: row => row.messageCount,
        cell: ({ row }: CellContext<WebsiteChatSession, unknown>) => (
          <td className="hidden sm:table-cell py-4 pr-3 w-16 text-sm text-neutral-500 text-right">
            {row.original.messageCount}
          </td>
        ),
        meta: { headerClassName: "hidden sm:table-cell w-16 text-right" },
        enableSorting: true
      },
      {
        id: "action",
        header: "",
        enableSorting: false,
        meta: { headerClassName: "w-12 sr-only" },
        cell: ({ row }: CellContext<WebsiteChatSession, unknown>) => (
          <td className="px-5 py-3 text-right">
            <Link
              target="_blank"
              to={`/conversations/website-chat/${row.original._id}`}
              className="inline-flex text-neutral-400 hover:text-purple-400 transition-colors"
              title="Open in Conversations"
            >
              <ExternalLink className="w-4 h-4" />
            </Link>
          </td>
        )
      }
    ],
    [brandMap, isBrandsLoading]
  )

  return (
    <Table<WebsiteChatSession>
      isLoading={isLoading}
      data={sessions}
      columns={columns}
      viewType="table"
      searchPlaceholder="Search by session ID or brand…"
      onSearchChange={onSearchChange}
      pagination={pagination}
      setPagination={setPagination}
      pageCount={pageCount}
      rowCount={totalRows}
      pageSize={pagination.pageSize}
      extraHeaderElement={
        <DateFilter
          selectedRange={datePreset}
          customStartDate={customDateStart ? new Date(customDateStart) : null}
          customEndDate={customDateEnd ? new Date(customDateEnd) : null}
          onRangeChange={onDateChange}
          onClearFilter={onClearDate}
        />
      }
      extraHeaderElementClassName="ml-auto"
      shrinkFilterSort
      defaultSortBy="lastActivity"
      defaultSortDir="desc"
      noSorter={false}
    />
  )
}

export default WebsiteChatsTable
